class Helpers {
  /**
   * Slide up animation
   *
   * @param {Element|HTMLElement} element
   * @param {number} duration
   * @return {Promise<boolean>} return promise
   */
  static slideUp(element, duration = 300) {
    return new Promise(function (resolve) {
      element.style.height = element.offsetHeight + 'px';
      element.style.transitionProperty = `height, margin, padding`;
      element.style.transitionDuration = duration + 'ms';
      element.style.overflow = 'hidden';
      element.style.height = '0';
      element.style.paddingTop = '0';
      element.style.paddingBottom = '0';
      element.style.marginTop = '0';
      element.style.marginBottom = '0';
      window.setTimeout(() => {
        element.style.display = 'none';
        element.style.removeProperty('height');
        element.style.removeProperty('padding-top');
        element.style.removeProperty('padding-bottom');
        element.style.removeProperty('margin-top');
        element.style.removeProperty('margin-bottom');
        element.style.removeProperty('overflow');
        element.style.removeProperty('transition-duration');
        element.style.removeProperty('transition-property');

        resolve(true);
      }, duration);
    });
  };

  /**
   * Slide down animation
   *
   * @param {Element|HTMLElement} element
   * @param {number} duration
   * @return {Promise<boolean>} return promise
   */
  static slideDown(element, duration = 300) {
    return new Promise(function (resolve) {
      element.style.removeProperty('display');

      if (window.getComputedStyle(element).display === 'none') {
        element.style.display = 'block';
      } else {
        element.style.display = 'none';
      }

      element.style.overflow = 'hidden';
      element.style.height = '0';
      element.style.paddingTop = '0';
      element.style.paddingBottom = '0';
      element.style.marginTop = '0';
      element.style.marginBottom = '0';
      element.style.transitionProperty = 'height, margin, padding';
      element.style.transitionDuration = duration + 'ms';
      element.style.height = element.offsetHeight + 'px';
      element.style.removeProperty('padding-top');
      element.style.removeProperty('padding-bottom');
      element.style.removeProperty('margin-top');
      element.style.removeProperty('margin-bottom');
      window.setTimeout(() => {
        element.style.removeProperty('height');
        element.style.removeProperty('overflow');
        element.style.removeProperty('transition-duration');
        element.style.removeProperty('transition-property');

        resolve(true);
      }, duration)
    });
  };

  /**
   * SlideToggle
   *
   * @param {Element|HTMLElement} element
   * @param {number} duration
   * @return {Promise<boolean>} return promise
   */
  static slideToggle(element, duration = 500) {
    if (window.getComputedStyle(element).display === 'none') {
      return this.slideDown(element, duration);
    }

    return this.slideUp(element, duration);
  };

  /**
   * Fade in animation
   *
   * @param {HTMLElement} el
   * @param {boolean} smooth
   * @param {string} displayStyle
   */
  static fadeIn(el, smooth = true, displayStyle = 'block') {
    if (smooth && el.style.opacity === 0) {
      this.fadeInEl(el, displayStyle);
    } else {
      el.style.display = displayStyle;
      el.style.opacity = 1;
    }
  };

  /**
   * Fade out animation
   *
   * @param {HTMLElement} el
   * @param {boolean} smooth
   * @param {string} displayStyle
   */
  static fadeOut(el, smooth = true, displayStyle = 'none') {
    if (smooth && el.style.opacity === 1) {
      this.fadeOutEl(el, displayStyle);
    } else {
      el.style.display = displayStyle;
      el.style.opacity = 0;
    }
  };

  /**
   * Fade in helper
   *
   * @param {HTMLElement} el
   * @param {string} displayStyle
   */
  static fadeInEl(el, displayStyle) {
    let opacity = 0;
    let request;

    const animation = () => {
      opacity += 0.04;

      el.style.opacity = opacity;

      if (opacity >= 1) {
        opacity = 1;

        cancelAnimationFrame(request);
      }
    };

    const rAf = () => {
      request = requestAnimationFrame(rAf);
      animation();
    };

    el.style.display = displayStyle;

    rAf();
  };

  /**
   * Fade out helper
   *
   * @param {HTMLElement} el
   * @param {string} displayStyle
   */
  static fadeOutEl(el, displayStyle) {
    let opacity = el.style.opacity;
    let request;

    const animation = () => {
      opacity -= 0.04;

      el.style.opacity = opacity;

      if (opacity <= 0) {
        opacity = 0;

        el.style.display = displayStyle;

        cancelAnimationFrame(request);
      }
    };

    const rAf = () => {
      request = requestAnimationFrame(rAf);
      animation();
    };
    rAf();
  };
}

export default Helpers;
